import { Fragment, lazy, Suspense, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, Outlet } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ClickAwayListener, Snackbar } from '@mui/material';
import { User, Company, Group } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import moment from 'moment';
import DefaultContent from '../shared/template/DefaultContent';
import { auth } from '../../lib/google/firebase';
import NavBarWeb from './NavBarWeb';
import NavBarApp from './NavBarApp';
import { CompanyContext } from '../../context/CompanyContext';
import { UserContext } from '../../context/UserContext';
import FooterWebsite from './FooterWebsite';
import { getUserDetails } from '../../lib/user_management/users';
import FeedbackSnackbarProvider from '../../context/FeedbackSnackbarContext';
import FeedbackSnackbar from './FeedbackSnackbar';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import CookieConsentDrawer from './CookieConsentDrawer';
import Redirect from './Redirect';
import { GroupContext } from '../../context/GroupContext';
import { log } from '../../util/log';
import { generateUuidV4 } from '../../util/uuid';
import { CompanyExtended } from '../../lib/app/company';
import { default_authenticated_home_url } from '../routing/Routes';
import SideMenuNavItems from './SideMenuNavItems/SideMenuNavItems';
import LoginModal from './authentication/LoginModal';
import { DrawerLeftContext } from '../../context/DrawerLeftContext';
import { SubscriptionProvider } from '../../context/SubscriptionContext';
import PBIRefreshProvider from '../../context/PBIRefreshContext';
import PageTracker from '../analytics/PageTracker';
import { getCompanies } from '../../graphql/services/company.service';
import {
  COMPONENT_UNKNOWN_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../util/global_variables';
import { updateUserLastAccessed } from '../../graphql/services/user.service';
import { ResolverMutationRequestMetadata, UpdateUserClientData } from '@esg/syntiro-api-core';
import { isMicrosoftUser, UserWithMfaInfo } from '../../lib/auth/auth.util';
import {
  getSsoExpectedEmail,
  isAuthLinkingInProgress,
  setSsoExpectedEmail
} from '../../lib/google/firebase';
import { CommentsPanelParameters, DashboardContext } from '../../context/DashboardContext';
import AskSyntiroContextProvider from '../../context/AskSyntiroContext';
import { PBIReport } from '@esg/esg-global-types';
import usePbiTokenManager from '../../hooks/usePbiTokenManager';
import { useCloseDrawerOnIframeFocus } from '../../hooks/use-close-drawer-on-iframe-focus';
import { EMBEDDED_CONTENT_IFRAME_SELECTOR } from '../../lib/app/embedded-content';
import AskSyntiroPanel from '../../features/ask-syntiro/components/AskSyntiroPanel';
const DashboardRenderLayer = lazy(() => import('./DashboardRenderLayer'));

const SOURCE = 'AppBase.tsx';

const DrawerBody = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flexGrow: 1,
  overflow: 'hidden',
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0
  })
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end'
}));

/**
 * Base container for all app functionality and views.
 * @returns {JSX.Element}
 */
const AppBase = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user, setUser, authenticated, setAuthenticated } = useContext(UserContext);
  const { isOpenDrawerLeft, closeDrawerLeft } = useContext(DrawerLeftContext);
  const [displayLoginModal, setDisplayLoginModal] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Array<CompanyExtended> | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [open_snackbar_welcome_message, setOpenSnackbarWelcomeMessage] = useState(false);
  const [open_snackbar_company_select, setOpenSnackbarCompanySelect] = useState(false);
  const [openAssist, setOpenAssist] = useState(false);
  const [priorityReports, setPriorityReports] = useState<Array<PBIReport>>([]);
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false);
  const [commentsPanelParameters, setCommentsPanelParameters] =
    useState<CommentsPanelParameters | null>(null);

  const navigate = useNavigate();
  const { validPbiReports, invalidPbiReports, dashboardStatus, lastTokenRefresh } =
    usePbiTokenManager(user, company, group);

  const left_menu_width = 350;

  // Handles if the Login Modal state is set to Show or to Hide the Login Modal.
  const handleDisplayLoginModal = (display: boolean): void => {
    setDisplayLoginModal(display);
  };

  // Sets the state of the welcome snackbar to false if user clicks the close button on the snackbar.
  const handleSnackbarCloseWelcomeMessage = (
    event: SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbarWelcomeMessage(false);
  };

  // Handles when a user changes groups based on company select drop down, also sets local storage tracking value.
  const handleGroupChange = (group: Group | null): void => {
    try {
      if (group) {
        localStorage.setItem('current_group', group.id);
        setGroup(group);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: Appbase failed on an unknown error while calling handleGroupChange.',
          { group: group },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to change group. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  // Adds companies in memory to populate the company selector dropdown without refresh.
  const addGroupCompany = (new_company: CompanyExtended): void => {
    if (companies) {
      setCompanies([new_company, ...companies]);
    }
  };

  // Removes companies in memory to populate the company selector dropdown without refresh.
  const removeGroupCompany = (deleted_company: Company): void => {
    if (companies) {
      setCompanies(companies.filter((company) => company.id !== deleted_company.id));
    }
  };

  // Removes all companies in memory associated to a deleted group in the company selector without refresh.
  const removeGroup = (deleted_group: Group): void => {
    if (companies) {
      setCompanies(companies.filter((company) => company.group.id !== deleted_group.id));
    }
  };

  // Handles when a user changes select companies based on company select drop down, also sets local storage tracking value.
  const handleCompanyChange = (company: Company | null): void => {
    try {
      if (company) {
        setOpenSnackbarCompanySelect(true);
        localStorage.setItem('current_company', company.id);
        setCompany(company);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: Appbase failed on an unknown error while calling handleCompanyChange.',
          { company: company },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to change company. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const resetAppContext = () => {
    setUser(null);
    setGroup(null);
    setCompany(null);
  };

  const handleDrawerCloseAssist = () => {
    setOpenAssist(false);
  };

  // ClickAwayListener below cannot see clicks inside an embedded report iframe.
  useCloseDrawerOnIframeFocus(isOpenDrawerLeft, closeDrawerLeft, EMBEDDED_CONTENT_IFRAME_SELECTOR);

  const openCommentsPanel = (parameters: CommentsPanelParameters) => {
    setCommentsPanelParameters(parameters);
    setCommentsPanelOpen(true);
    setOpenAssist(false);
  };

  const closeCommentsPanel = () => {
    setCommentsPanelOpen(false);
  };

  // A panel opened against one company must not survive a tenant switch.
  useEffect(() => {
    setCommentsPanelOpen(false);
  }, [company?.id, group?.id, user?.id]);

  // The comments panel and the left nav drawer both anchor to the left edge —
  // opening the drawer closes comments. Ask Syntiro is anchored right, so it
  // is unaffected and can stay open alongside the drawer.
  useEffect(() => {
    if (isOpenDrawerLeft) {
      setCommentsPanelOpen(false);
    }
  }, [isOpenDrawerLeft]);

  const handleSnackbarCloseCompanySelect = (event: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbarCompanySelect(false);
  };

  // Auto sign out user after X time from last click.
  useEffect(() => {
    const handleClick = (): void => {
      const last_activity: string | null = localStorage.getItem('last_activity');
      if (last_activity) {
        if (moment().unix() - Number(last_activity) > 14440) {
          alert('Session expired, you will be signed out and returned to the homepage');
          closeDrawerLeft();
          localStorage.removeItem('last_activity');
          auth.signOut();
          return;
        }
      }
      if (auth.currentUser) {
        localStorage.setItem('last_activity', moment().unix().toString());
      }
    };
    handleClick();
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  // Add global firebase auth listener for auth state changes.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Do not flash into authenticated app state if temporary sign-in is used for account provider linking.
      if (isAuthLinkingInProgress()) return;
      try {
        if (user) {
          // Check user did not manually enter a different email to their browser cached sso email.
          const sso_expected_email: string | null = getSsoExpectedEmail();
          setSsoExpectedEmail(null);
          if (
            sso_expected_email &&
            user.email?.toLowerCase() !== sso_expected_email.toLowerCase()
          ) {
            await signOut(auth);
            return;
          }
        }
        const user_info: UserWithMfaInfo | null = user;
        if (!user || (!isMicrosoftUser(user) && user_info?.reloadUserInfo?.mfaInfo === undefined)) {
          localStorage.clear();
          setAuthenticated(false);
          return;
        }
        setAuthenticated(true);
        localStorage.setItem('last_activity', moment().unix().toString());
        if (location.pathname === '/') {
          navigate(default_authenticated_home_url);
        }
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: Appbase failed on an unknown error while initialising.',
            { user: user },
            tracking_id
          )
        );
        setFeedbackData({
          message: `Failed to verify user. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
    });

    return unsubscribe;
  }, []);

  // Fetch user authorization claims with user firestore document if exists.
  useEffect(() => {
    (async () => {
      try {
        if (auth.currentUser) {
          await getUserDetails(auth.currentUser.uid)
            .then((user_data: User) => {
              setUser({ ...user_data });
            })
            .catch((err) => {
              throw new Error(err);
            });
        }
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: Appbase failed on an unknown error while initialising.',
            { 'auth.currentUser': auth.currentUser },
            tracking_id
          )
        );
        setFeedbackData({
          message: `Application failed to load user. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
      return null;
    })();
  }, [authenticated]);

  const fetchCompanies = async (): Promise<Array<CompanyExtended> | undefined> => {
    try {
      const companies: Array<CompanyExtended> = await getCompanies();
      return companies;
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchCompanies'),
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Failed to load companies', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  // Update user last accessed on successful login and fetch group and company data for app.
  useEffect(() => {
    (async () => {
      try {
        if (authenticated && user) {
          const request_metadata: ResolverMutationRequestMetadata = {
            tracking_id: generateUuidV4()
          };
          const user_details: UpdateUserClientData = { id: user.id };
          await updateUserLastAccessed(request_metadata, user_details);
          const companies: Array<CompanyExtended> | undefined = await fetchCompanies();
          if (companies) {
            setCompanies(companies);
          }
        }
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
            tracking_id
          )
        );
        setFeedbackData({
          message: FEEDBACK_ACTION_FAILURE('Failed to initialise application', tracking_id),
          state: true,
          type: 'error'
        });
      }
      return;
    })();
  }, [user]);

  const snackbar_action = (
    <Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSnackbarCloseCompanySelect}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Fragment>
  );

  return (
    <>
      <PageTracker />

      <FeedbackSnackbarProvider>
        <GroupContext.Provider value={{ group, handleGroupChange }}>
          <CompanyContext.Provider
            value={{
              company,
              handleCompanyChange,
              addGroupCompany,
              removeGroupCompany,
              removeGroup
            }}
          >
            <DashboardContext.Provider
              value={{
                validPbiReports,
                invalidPbiReports,
                dashboardStatus,
                priorityReports,
                setPriorityReports,
                lastTokenRefresh,
                commentsPanelOpen,
                commentsPanelParameters,
                openCommentsPanel,
                closeCommentsPanel
              }}
            >
              <SubscriptionProvider>
                <AskSyntiroContextProvider>
                  <Redirect />
                  <LoginModal
                    showLoginModal={displayLoginModal}
                    setShowLoginModal={setDisplayLoginModal}
                    setAuthenticated={setAuthenticated}
                  />
                  <ClickAwayListener
                    mouseEvent="onMouseDown"
                    touchEvent="onTouchStart"
                    onClickAway={(event) => {
                      if (!isOpenDrawerLeft) return;
                      if (
                        event.target instanceof Element &&
                        event.target.closest('[data-ask-syntiro-panel]')
                      ) {
                        return;
                      }
                      closeDrawerLeft();
                    }}
                  >
                    <Drawer
                      sx={{
                        width: left_menu_width,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                          width: left_menu_width,
                          boxSizing: 'border-box'
                        }
                      }}
                      variant="persistent"
                      anchor="left"
                      open={isOpenDrawerLeft}
                    >
                      <Box sx={{ my: 1 }}>
                        <SideMenuNavItems handleCloseNav={() => closeDrawerLeft()} />
                      </Box>
                    </Drawer>
                  </ClickAwayListener>
                  <PBIRefreshProvider>
                    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
                      {authenticated === false && <NavBarWeb />}
                      {authenticated && (
                        <NavBarApp
                          companies={companies}
                          left_menu_width={left_menu_width}
                          resetAppContext={() => resetAppContext()}
                        />
                      )}

                      {/* App Content Screen */}
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          minHeight: 0
                        }}
                      >
                        <DrawerBody>
                          {authenticated && <DrawerHeader />}
                          {authenticated && (
                            <Suspense fallback={null}>
                              <DashboardRenderLayer />
                            </Suspense>
                          )}
                          <Outlet context={{ handleDisplayLoginModal }} />
                        </DrawerBody>
                      </Box>

                      {authenticated && <AskSyntiroPanel />}

                      {/* Assistance Drawer */}
                      <Drawer anchor="right" open={openAssist} onClose={handleDrawerCloseAssist}>
                        <Toolbar variant="dense">
                          <Typography variant="h6" color="inherit" component="div">
                            Assistance
                          </Typography>
                        </Toolbar>
                        <Box
                          sx={{
                            width: 300,
                            backgroundColor: 'white',
                            padding: 3
                          }}
                        >
                          <DefaultContent></DefaultContent>
                        </Box>
                      </Drawer>
                      <Snackbar
                        open={open_snackbar_company_select}
                        autoHideDuration={6000}
                        onClose={handleSnackbarCloseCompanySelect}
                        message={`Viewing as ${company?.name ? company?.name : 'Default'}`}
                        action={snackbar_action}
                      />
                      {authenticated === false && (
                        <FooterWebsite handleDisplayLoginModal={handleDisplayLoginModal} />
                      )}
                    </Box>
                  </PBIRefreshProvider>
                  <CookieConsentDrawer />
                  <Snackbar
                    open={open_snackbar_welcome_message}
                    autoHideDuration={6000}
                    onClose={handleSnackbarCloseWelcomeMessage}
                    message={'User signed in.'}
                    action={snackbar_action}
                  />
                  <FeedbackSnackbar />
                </AskSyntiroContextProvider>
              </SubscriptionProvider>
            </DashboardContext.Provider>
          </CompanyContext.Provider>
        </GroupContext.Provider>
      </FeedbackSnackbarProvider>
    </>
  );
};

export default AppBase;
